/**
 * ActivateCycleBanner — Iter. 406.p
 *
 * Permanent hero banner pinned at the TOP of the Dashboard that surfaces
 * the AI plan creation CTA whenever the user has no active 6-week cycle.
 * Disappears the moment a cycle is active.
 *
 * Interaction model
 * ─────────────────
 *   • Tap CTA  → opens the WorkoutOnboardingModal (Iter. 405.h wizard).
 *   • "Plus tard" or X → POST /onboarding/dismiss-plan-cta to mute it
 *     until the user explicitly re-enables via the nav menu entry.
 *   • The "Activer mon cycle 6 semaines" nav entry calls
 *     /onboarding/restore-plan-cta to bring this hero back.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Rocket, X, ChevronRight } from 'lucide-react';
import { api } from '../context/AppContext';

export default function ActivateCycleBanner({ language = 'fr', onOpenWizard }) {
  const fr = language === 'fr';
  const [state, setState] = useState(null);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/onboarding/state');
      setState(res?.data || null);
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    load();
  }, [load]);

  // Allow parent to refresh after wizard success
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('yumix:onboarding-refresh', handler);
    return () => window.removeEventListener('yumix:onboarding-refresh', handler);
  }, [load]);

  const handleDismiss = useCallback(async () => {
    try {
      await api.post('/onboarding/dismiss-plan-cta', { source: 'dashboard_banner' });
    } catch { /* dismissal is best-effort */ }
    setState((s) => (s ? { ...s, plan_cta_dismissed: true } : s));
  }, []);

  if (!state) return null;
  if (state.has_active_cycle) return null;
  if (state.plan_cta_dismissed) return null;

  return (
    <div
      className="relative rounded-2xl overflow-hidden border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/25 via-orange-500/15 to-rose-500/20 shadow-[0_0_40px_rgba(251,191,36,0.25)]"
      data-testid="activate-cycle-banner"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full bg-amber-400/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-rose-500/25 blur-3xl" />

      <button
        type="button"
        onClick={handleDismiss}
        aria-label={fr ? 'Plus tard' : 'Later'}
        data-testid="activate-cycle-banner-dismiss"
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/30 hover:bg-black/60 text-white flex items-center justify-center"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="relative p-4 pr-10">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-200" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200 font-bold">
            EXTRA MILES
          </p>
        </div>
        <h2 className="font-heading uppercase text-base sm:text-lg text-white leading-tight mb-1.5">
          {fr ? 'Prêt à passer au niveau supérieur ?' : 'Ready to level up?'}
        </h2>
        <p className="text-xs text-white/80 mb-3 leading-snug">
          {fr
            ? 'Démarre ton programme d\'entraînement personnalisé sur 6 semaines dès maintenant.'
            : 'Kickstart your personalized 6-week training program right now.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => { if (onOpenWizard) onOpenWizard(); }}
            data-testid="activate-cycle-banner-cta"
            className="flex-1 h-11 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black font-heading uppercase tracking-widest text-xs flex items-center justify-center gap-1.5 shadow-[0_0_20px_rgba(251,191,36,0.45)] hover:scale-[1.01] transition-transform"
          >
            <Rocket className="w-4 h-4" />
            {fr ? 'Activer mon cycle 6 semaines' : 'Activate my 6-week cycle'}
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="activate-cycle-banner-later"
            className="sm:w-32 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white/80 text-xs uppercase tracking-widest font-bold"
          >
            {fr ? 'Plus tard' : 'Later'}
          </button>
        </div>
      </div>
    </div>
  );
}
